import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import icalService from '../../../services/icalService';
import { ICal, House } from '../../../services/types';

interface ICalModalProps {
    isOpen: boolean;
    onClose: () => void;
    ical: ICal | null;
    houses: House[];
}

const ICalModal: React.FC<ICalModalProps> = ({ isOpen, onClose, ical, houses }) => {
    const [formData, setFormData] = useState<Partial<ICal>>({
        name: '',
        url: '',
        house_id: '',
        active: true,
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (ical) {
            setFormData({
                name: ical.name,
                url: ical.url,
                house_id: ical.house_id,
                active: ical.active,
            });
        } else {
            setFormData({
                name: '',
                url: '',
                house_id: houses.length > 0 ? houses[0].public_id : '',
                active: true,
            });
        }
        setErrors({});
    }, [ical, houses, isOpen]);

    const validate = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name || formData.name.trim() === '') {
            newErrors.name = 'Name is required';
        }

        if (!formData.url || formData.url.trim() === '') {
            newErrors.url = 'URL is required';
        } else if (!isValidUrl(formData.url)) {
            newErrors.url = 'Please enter a valid URL';
        }

        if (!formData.house_id || formData.house_id === '' || formData.house_id === '0') {
            newErrors.house_id = 'Please select a house';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isValidUrl = (url: string) => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setLoading(true);

        try {
            if (ical?.public_id) {
                await icalService.update(ical.public_id, formData);
            } else {
                await icalService.create(formData);
            }
            onClose();
        } catch (error) {
            console.error('Error saving iCal:', error);
            alert('Failed to save iCal');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="modal fade show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={onClose}
        >
            <div
                className="modal-dialog modal-dialog-centered"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {ical ? 'Edit iCal' : 'Create New iCal'}
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            aria-label="Close"
                        />
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label htmlFor="name" className="form-label">
                                    Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Enter iCal name"
                                />
                                {errors.name && (
                                    <div className="invalid-feedback">{errors.name}</div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label htmlFor="url" className="form-label">
                                    iCal URL <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="url"
                                    className={`form-control ${errors.url ? 'is-invalid' : ''}`}
                                    id="url"
                                    name="url"
                                    value={formData.url}
                                    onChange={handleChange}
                                    placeholder="https://example.com/calendar.ics"
                                />
                                {errors.url && (
                                    <div className="invalid-feedback">{errors.url}</div>
                                )}
                                <small className="form-text text-muted">
                                    Enter the full iCal feed URL
                                </small>
                            </div>

                            <div className="mb-3">
                                <label htmlFor="house_id" className="form-label">
                                    House <span className="text-danger">*</span>
                                </label>
                                <select
                                    className={`form-select ${errors.house_id ? 'is-invalid' : ''}`}
                                    id="house_id"
                                    name="house_id"
                                    value={formData.house_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Select a house</option>
                                    {houses.map((house) => (
                                        <option key={house.public_id} value={house.public_id}>
                                            {house.name || house.address}
                                        </option>
                                    ))}
                                </select>
                                {errors.house_id && (
                                    <div className="invalid-feedback">{errors.house_id}</div>
                                )}
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        Saving...
                                    </>
                                ) : (
                                    ical ? 'Update iCal' : 'Create iCal'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ICalModal;
